// src/context/ThemeContext.jsx
import {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';

const ThemeContext = createContext();

// Hook to consume theme context.
// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Provider component to manage and provide dark mode state.
export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    // Initialize dark mode from localStorage or system preference.
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Effect to apply/remove 'dark' class on <html> and persist theme to localStorage.
  useEffect(() => {
    const htmlElement = window.document.documentElement;

    if (darkMode) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Toggles the dark mode state.
  const toggleDarkMode = useCallback(() => {
    setDarkMode((prevMode) => !prevMode);
  }, []);

  // Memoizes the context value to prevent unnecessary re-renders.
  const value = useMemo(() => ({ darkMode, toggleDarkMode }), [darkMode, toggleDarkMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// PropTypes validation for ThemeProvider
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
